/**
 * An organization's logo, or its initials when it has none.
 *
 * The image is fetched with the user's token (logos aren't public), once
 * per logo version, and shared across the app. See
 * docs/frontend/components/organization-logo.md.
 */
import { useEffect, useState } from 'react';
import { leasApi } from '../../api/leas';
import { cachedLogoUrl, loadLogoUrl, organizationInitials } from '../../utils/organizationLogo';

const SIZES = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-12 w-12 text-sm',
  lg: 'h-16 w-16 text-lg',
};

interface OrganizationLogoProps {
  leaId: number;
  name: string;
  /** From the LEA response; null or missing means no logo */
  logoUpdatedAt?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}

export const OrganizationLogo: React.FC<OrganizationLogoProps> = ({
  leaId,
  name,
  logoUpdatedAt,
  size = 'md',
  className = '',
}) => {
  const [url, setUrl] = useState<string | null>(() =>
    logoUpdatedAt ? cachedLogoUrl(leaId, logoUpdatedAt) ?? null : null
  );

  useEffect(() => {
    if (!logoUpdatedAt) {
      setUrl(null);
      return;
    }
    let active = true;
    setUrl(cachedLogoUrl(leaId, logoUpdatedAt) ?? null);
    loadLogoUrl(leaId, logoUpdatedAt, leasApi.getLogo).then((loaded) => {
      if (active) setUrl(loaded);
    });
    return () => {
      active = false;
    };
  }, [leaId, logoUpdatedAt]);

  // The name is always shown beside the logo, so both forms are decorative.
  // A light tile in dark mode keeps dark logos on transparent backgrounds visible.
  if (url) {
    return (
      <img
        src={url}
        alt=""
        aria-hidden="true"
        className={`${SIZES[size]} shrink-0 rounded-md object-contain dark:bg-gray-100 dark:p-0.5 ${className}`}
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className={`${SIZES[size]} inline-flex shrink-0 select-none items-center justify-center rounded-md bg-blue-100 font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 ${className}`}
    >
      {organizationInitials(name)}
    </span>
  );
};
